import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * CLAÈ Admin — Stock Management (admin-only).
 *
 * Simple inventory for consumables used per treatment.
 *   • Table: Item | Treatment(s) | Stock | Min | Status
 *   • "Need to Buy" summary chip filters to Low / Out of Stock items.
 *   • Add / Edit modal with per-treatment usage rows.
 *   • Manual Adjust (+/-) modal with optional note.
 *   • History drawer: auto deductions (per Completed appt) + manual adjustments.
 *
 * Stock deducts automatically ONLY when a booking's appointment_status
 * changes to "Completed" (server-side, in _apply_completed_automations).
 */

const STATUS_META = {
  in_stock:     { label: "In Stock",     cls: "text-emerald-700" },
  low_stock:    { label: "Low Stock",    cls: "text-amber-700" },
  out_of_stock: { label: "Out of Stock", cls: "text-red-700" },
};

export default function AdminStock() {
  const [rows, setRows] = useState([]);
  const [needToBuy, setNeedToBuy] = useState(0);
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all | need
  const [query, setQuery] = useState("");

  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);

  const [showAdjust, setShowAdjust] = useState(false);
  const [adjustTarget, setAdjustTarget] = useState(null);

  const [showHistory, setShowHistory] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/stock");
      setRows(r.data?.items || []);
      setNeedToBuy(r.data?.need_to_buy || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load stock");
    }
    setLoading(false);
  }, []);

  const loadTreatments = useCallback(async () => {
    try {
      const r = await api.get("/treatments/all");
      setTreatments(r.data || []);
    } catch (e) {
      /* non-fatal — editor just won't offer suggestions */
    }
  }, []);

  useEffect(() => { load(); loadTreatments(); }, [load, loadTreatments]);

  const filtered = useMemo(() => {
    let out = rows;
    if (filter === "need") {
      out = out.filter((r) => r.status !== "in_stock");
    }
    const q = query.trim().toLowerCase();
    if (q) {
      out = out.filter(
        (r) =>
          (r.name || "").toLowerCase().includes(q) ||
          (r.treatment_usage || []).some((u) => (u.treatment_name || "").toLowerCase().includes(q)),
      );
    }
    return out;
  }, [rows, filter, query]);

  const openCreate = () => { setEditing(null); setShowEditor(true); };
  const openEdit = (row) => { setEditing(row); setShowEditor(true); };
  const openAdjust = (row) => { setAdjustTarget(row); setShowAdjust(true); };
  const openHistory = async (row) => {
    setHistoryTarget(row); setShowHistory(true); setHistoryLoading(true);
    try {
      const r = await api.get("/admin/stock/history", {
        params: row ? { stock_item_id: row.id } : {},
      });
      setHistory(r.data || []);
    } catch (e) {
      toast.error("Could not load history");
      setHistory([]);
    }
    setHistoryLoading(false);
  };
  const openGlobalHistory = () => openHistory(null);

  const onDelete = async (row) => {
    if (!window.confirm(`Delete stock item "${row.name}"? The history log will be kept.`)) return;
    try {
      await api.delete(`/admin/stock/${row.id}`);
      toast.success("Stock item deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-stock">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-serif text-2xl text-stone-900">Stock</h2>
          <p className="text-xs text-stone-600 mt-1">
            Simple inventory. Stock deducts automatically only when an appointment is marked <strong>Completed</strong>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="stock-view-history-btn"
            onClick={openGlobalHistory}
            className="mono-label text-xs border border-stone-900 px-3 py-2 hover:bg-stone-900 hover:text-white transition"
          >History</button>
          <button
            data-testid="stock-add-item-btn"
            onClick={openCreate}
            className="mono-label text-xs border border-stone-900 bg-stone-900 text-white px-4 py-2 hover:bg-stone-700 transition"
          >+ Add stock item</button>
        </div>
      </div>

      {/* Need-to-buy chip + search */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          data-testid="stock-need-to-buy-chip"
          onClick={() => setFilter(filter === "need" ? "all" : "need")}
          className={`inline-flex items-center gap-2 border hairline px-4 py-2 mono-label text-xs transition ${
            filter === "need"
              ? "bg-stone-900 text-white border-stone-900"
              : "bg-white text-stone-900 hover:bg-stone-50"
          }`}
          title="Click to filter the list"
        >
          <span aria-hidden>🛒</span>
          <span>Need to Buy: {needToBuy} {needToBuy === 1 ? "item" : "items"}</span>
          {filter === "need" && <span className="opacity-70">· Clear</span>}
        </button>
        <input
          data-testid="stock-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search items or treatments…"
          className="border hairline px-3 py-2 text-sm w-72 focus:outline-none focus:border-stone-900"
        />
      </div>

      {/* Table */}
      <div className="border hairline bg-white">
        <div className="grid grid-cols-[2fr_2.3fr_90px_90px_100px_120px_auto] gap-3 px-5 py-3 border-b hairline mono-label text-[11px] text-stone-600">
          <span>ITEM</span>
          <span>TREATMENT(S)</span>
          <span>STOCK</span>
          <span>MIN</span>
          <span>UNIT COST</span>
          <span>STATUS</span>
          <span className="text-right">ACTIONS</span>
        </div>
        {loading && <div className="px-5 py-6 text-sm text-stone-500">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="px-5 py-6 text-sm text-stone-500">
            {rows.length === 0 ? "No stock items yet. Click “Add stock item” to begin." : "No items match this filter."}
          </div>
        )}
        {!loading && filtered.map((r) => {
          const meta = STATUS_META[r.status] || STATUS_META.in_stock;
          return (
            <div
              key={r.id}
              data-testid={`stock-row-${r.id}`}
              className="grid grid-cols-[2fr_2.3fr_90px_90px_100px_120px_auto] gap-3 px-5 py-3 border-b hairline items-center text-sm"
            >
              <span className="font-medium text-stone-900">
                {r.name}
                {r.notes && (
                  <span className="block text-[11px] text-stone-500 font-normal truncate" title={r.notes}>
                    {r.notes}
                  </span>
                )}
              </span>
              <span className="text-xs text-stone-700">
                {(r.treatment_usage || []).length === 0
                  ? <span className="text-stone-400">— not linked —</span>
                  : (r.treatment_usage || []).map((u, i) => (
                      <span key={u.treatment_id || i} className="inline-block mr-2 mb-1">
                        <span className="text-stone-900">{u.treatment_name || u.treatment_id}</span>
                        <span className="text-stone-500"> · {u.qty_per_treatment}×</span>
                      </span>
                    ))}
              </span>
              <span className="tabular-nums">{r.current_qty}</span>
              <span className="tabular-nums text-stone-500">{r.min_qty}</span>
              <span className="tabular-nums text-stone-700">£{Number(r.cost_per_unit || 0).toFixed(2)}</span>
              <span className={`mono-label text-[11px] ${meta.cls}`} data-testid={`stock-status-${r.id}`}>{meta.label}</span>
              <div className="flex gap-2 justify-end">
                <button
                  data-testid={`stock-adjust-${r.id}`}
                  onClick={() => openAdjust(r)}
                  className="mono-label text-[11px] border border-stone-900 px-3 py-1 hover:bg-stone-900 hover:text-white transition"
                >Adjust</button>
                <button
                  data-testid={`stock-edit-${r.id}`}
                  onClick={() => openEdit(r)}
                  className="mono-label text-[11px] border border-stone-900 px-3 py-1 hover:bg-stone-900 hover:text-white transition"
                >Edit</button>
                <button
                  data-testid={`stock-history-${r.id}`}
                  onClick={() => openHistory(r)}
                  className="mono-label text-[11px] border border-stone-400 text-stone-600 px-3 py-1 hover:bg-stone-100 transition"
                >Log</button>
                <button
                  data-testid={`stock-delete-${r.id}`}
                  onClick={() => onDelete(r)}
                  className="mono-label text-[11px] border border-red-700 text-red-700 px-3 py-1 hover:bg-red-700 hover:text-white transition"
                >Delete</button>
              </div>
            </div>
          );
        })}
      </div>

      {showEditor && (
        <StockEditorModal
          treatments={treatments}
          editing={editing}
          onClose={() => setShowEditor(false)}
          onSaved={() => { setShowEditor(false); load(); }}
        />
      )}

      {showAdjust && adjustTarget && (
        <AdjustModal
          item={adjustTarget}
          onClose={() => setShowAdjust(false)}
          onSaved={() => { setShowAdjust(false); load(); }}
        />
      )}

      {showHistory && (
        <HistoryModal
          item={historyTarget}
          rows={history}
          loading={historyLoading}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
}

/* ──────────────────────────── Editor ──────────────────────────── */
function StockEditorModal({ treatments, editing, onClose, onSaved }) {
  const [name, setName] = useState(editing?.name || "");
  const [currentQty, setCurrentQty] = useState(editing?.current_qty ?? 0);
  const [minQty, setMinQty] = useState(editing?.min_qty ?? 0);
  const [costPerUnit, setCostPerUnit] = useState(editing?.cost_per_unit ?? 0);
  const [notes, setNotes] = useState(editing?.notes || "");
  const [usage, setUsage] = useState(
    (editing?.treatment_usage || []).map((u) => ({
      treatment_id: u.treatment_id,
      qty_per_treatment: u.qty_per_treatment,
    })),
  );
  const [saving, setSaving] = useState(false);

  const treatmentOptions = useMemo(
    () => treatments.map((t) => ({ id: t.id, name: t.name })),
    [treatments],
  );

  const addUsageRow = () => setUsage([...usage, { treatment_id: "", qty_per_treatment: 1 }]);
  const updateRow = (i, patch) => setUsage(usage.map((u, idx) => (idx === i ? { ...u, ...patch } : u)));
  const removeRow = (i) => setUsage(usage.filter((_, idx) => idx !== i));

  const submit = async () => {
    const n = (name || "").trim();
    if (!n) { toast.error("Enter an item name"); return; }
    if (Number(currentQty) < 0 || Number(minQty) < 0 || Number(costPerUnit) < 0) { toast.error("Quantities and cost must be ≥ 0"); return; }
    // Validate usage rows: non-empty treatment and positive qty.
    const cleaned = usage
      .filter((u) => u.treatment_id)
      .map((u) => ({
        treatment_id: u.treatment_id,
        qty_per_treatment: Number(u.qty_per_treatment),
      }));
    for (const u of cleaned) {
      if (!(u.qty_per_treatment >= 0)) {
        toast.error("Quantity used per treatment must be ≥ 0");
        return;
      }
    }
    // De-dupe: same treatment can't appear twice.
    const seen = new Set();
    for (const u of cleaned) {
      if (seen.has(u.treatment_id)) {
        toast.error("A treatment is listed more than once — remove the duplicate.");
        return;
      }
      seen.add(u.treatment_id);
    }
    setSaving(true);
    try {
      const payload = {
        name: n,
        current_qty: Number(currentQty),
        min_qty: Number(minQty),
        cost_per_unit: Number(costPerUnit),
        notes: notes.trim(),
        treatment_usage: cleaned,
      };
      if (editing?.id) {
        await api.patch(`/admin/stock/${editing.id}`, payload);
        toast.success("Stock item updated");
      } else {
        await api.post("/admin/stock", payload);
        toast.success("Stock item created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
    setSaving(false);
  };

  return (
    <Modal onClose={onClose} title={editing ? "Edit stock item" : "Add stock item"} testid="stock-editor-modal">
      <div className="space-y-4">
        <Field label="Item name">
          <input
            data-testid="stock-field-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Microneedling Cartridge"
            className="border hairline px-3 py-2 text-sm w-full focus:outline-none focus:border-stone-900"
          />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Current stock">
            <input
              data-testid="stock-field-current"
              type="number" min="0" step="1"
              value={currentQty}
              onChange={(e) => setCurrentQty(e.target.value)}
              className="border hairline px-3 py-2 text-sm w-full focus:outline-none focus:border-stone-900"
            />
          </Field>
          <Field label="Minimum stock">
            <input
              data-testid="stock-field-min"
              type="number" min="0" step="1"
              value={minQty}
              onChange={(e) => setMinQty(e.target.value)}
              className="border hairline px-3 py-2 text-sm w-full focus:outline-none focus:border-stone-900"
            />
          </Field>
          <Field label="Cost per unit (£)">
            <input
              data-testid="stock-field-cost"
              type="number" min="0" step="0.01"
              value={costPerUnit}
              onChange={(e) => setCostPerUnit(e.target.value)}
              placeholder="0.00"
              className="border hairline px-3 py-2 text-sm w-full focus:outline-none focus:border-stone-900"
            />
          </Field>
        </div>
        <Field label="Notes (optional)">
          <input
            data-testid="stock-field-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. 36-pin cartridge, box of 10"
            className="border hairline px-3 py-2 text-sm w-full focus:outline-none focus:border-stone-900"
          />
        </Field>

        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="mono-label text-[11px] text-stone-600">USED FOR</p>
            <button
              data-testid="stock-add-usage-row"
              onClick={addUsageRow}
              className="mono-label text-[11px] border border-stone-900 px-3 py-1 hover:bg-stone-900 hover:text-white transition"
            >+ Add treatment</button>
          </div>
          {usage.length === 0 && (
            <p className="text-xs text-stone-500">
              Link this item to one or more treatments and set how many are used per treatment.
            </p>
          )}
          <div className="space-y-2">
            {usage.map((u, i) => (
              <div key={i} className="grid grid-cols-[1fr_110px_auto] gap-2 items-center">
                <select
                  data-testid={`stock-usage-treatment-${i}`}
                  value={u.treatment_id}
                  onChange={(e) => updateRow(i, { treatment_id: e.target.value })}
                  className="border hairline px-2 py-2 text-sm focus:outline-none focus:border-stone-900"
                >
                  <option value="">Select treatment…</option>
                  {treatmentOptions.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <input
                  data-testid={`stock-usage-qty-${i}`}
                  type="number" min="0" step="0.5"
                  value={u.qty_per_treatment}
                  onChange={(e) => updateRow(i, { qty_per_treatment: e.target.value })}
                  placeholder="Qty"
                  className="border hairline px-2 py-2 text-sm focus:outline-none focus:border-stone-900"
                />
                <button
                  data-testid={`stock-usage-remove-${i}`}
                  onClick={() => removeRow(i)}
                  className="mono-label text-[11px] border border-stone-400 text-stone-600 px-3 py-2 hover:bg-stone-100 transition"
                >Remove</button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="mono-label text-xs border border-stone-400 text-stone-700 px-4 py-2 hover:bg-stone-100 transition"
          >Cancel</button>
          <button
            data-testid="stock-save-btn"
            onClick={submit}
            disabled={saving}
            className="mono-label text-xs border border-stone-900 bg-stone-900 text-white px-4 py-2 hover:bg-stone-700 transition disabled:opacity-50"
          >{saving ? "Saving…" : (editing ? "Save changes" : "Create item")}</button>
        </div>
      </div>
    </Modal>
  );
}

/* ──────────────────────────── Adjust ──────────────────────────── */
function AdjustModal({ item, onClose, onSaved }) {
  const [delta, setDelta] = useState("");
  const [note, setNote] = useState("");
  const [sign, setSign] = useState("plus"); // plus | minus
  const [saving, setSaving] = useState(false);

  const parsed = Number(delta);
  const effective = Number.isFinite(parsed) ? (sign === "minus" ? -Math.abs(parsed) : Math.abs(parsed)) : 0;
  const preview = Number(item.current_qty) + effective;

  const submit = async () => {
    if (!effective) { toast.error("Enter a non-zero quantity"); return; }
    if (preview < 0) { toast.error("Resulting stock would be negative"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/stock/${item.id}/adjust`, { delta: effective, note: note.trim() });
      toast.success("Stock adjusted");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to adjust");
    }
    setSaving(false);
  };

  return (
    <Modal onClose={onClose} title={`Adjust: ${item.name}`} testid="stock-adjust-modal">
      <div className="space-y-4">
        <div className="text-sm text-stone-700">
          Current stock: <strong className="text-stone-900 tabular-nums">{item.current_qty}</strong>
        </div>
        <div className="grid grid-cols-[auto_1fr] gap-2 items-center">
          <div className="inline-flex border hairline">
            <button
              data-testid="stock-adjust-plus"
              onClick={() => setSign("plus")}
              className={`mono-label text-xs px-3 py-2 ${sign === "plus" ? "bg-stone-900 text-white" : "text-stone-700"}`}
            >+ Add</button>
            <button
              data-testid="stock-adjust-minus"
              onClick={() => setSign("minus")}
              className={`mono-label text-xs px-3 py-2 ${sign === "minus" ? "bg-stone-900 text-white" : "text-stone-700"}`}
            >− Remove</button>
          </div>
          <input
            data-testid="stock-adjust-delta"
            type="number" min="0" step="1"
            value={delta}
            onChange={(e) => setDelta(e.target.value)}
            placeholder="Quantity"
            className="border hairline px-3 py-2 text-sm w-full focus:outline-none focus:border-stone-900"
          />
        </div>
        <Field label="Note (optional)">
          <input
            data-testid="stock-adjust-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. New delivery received"
            className="border hairline px-3 py-2 text-sm w-full focus:outline-none focus:border-stone-900"
          />
        </Field>
        <div className="text-xs text-stone-600">
          New stock will be: <strong className="text-stone-900 tabular-nums">{Number.isFinite(preview) ? preview : "—"}</strong>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="mono-label text-xs border border-stone-400 text-stone-700 px-4 py-2 hover:bg-stone-100 transition"
          >Cancel</button>
          <button
            data-testid="stock-adjust-save-btn"
            onClick={submit}
            disabled={saving || !effective}
            className="mono-label text-xs border border-stone-900 bg-stone-900 text-white px-4 py-2 hover:bg-stone-700 transition disabled:opacity-50"
          >{saving ? "Saving…" : "Apply adjustment"}</button>
        </div>
      </div>
    </Modal>
  );
}

/* ──────────────────────────── History ──────────────────────────── */
function HistoryModal({ item, rows, loading, onClose }) {
  return (
    <Modal
      onClose={onClose}
      title={item ? `History: ${item.name}` : "Stock history"}
      testid="stock-history-modal"
      wide
    >
      {loading && <div className="text-sm text-stone-500">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="text-sm text-stone-500">No activity yet.</div>
      )}
      {!loading && rows.length > 0 && (
        <div className="border hairline divide-y">
          {rows.map((h) => (
            <div key={h.id} className="px-4 py-3 text-sm grid grid-cols-[150px_1fr_120px_90px] gap-3 items-center">
              <span className="text-xs text-stone-500 tabular-nums">
                {new Date(h.at).toLocaleString("en-GB")}
              </span>
              <span className="text-stone-800">
                <span className="font-medium">{h.stock_item_name}</span>
                {h.kind === "auto" ? (
                  <span className="block text-xs text-stone-500">
                    {h.treatment_name || "Treatment"} — {h.client_name || "client"}
                    {h.booking_reference ? ` · ${h.booking_reference}` : ""}
                  </span>
                ) : (
                  <span className="block text-xs text-stone-500">
                    Manual adjustment{h.note ? ` — ${h.note}` : ""}
                  </span>
                )}
              </span>
              <span className={`mono-label text-xs ${h.qty_change < 0 ? "text-red-700" : "text-emerald-700"}`}>
                {h.qty_change > 0 ? `+${h.qty_change}` : h.qty_change}
              </span>
              <span className="text-xs text-stone-500 tabular-nums text-right">
                → {h.balance_after}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-end pt-4">
        <button
          onClick={onClose}
          className="mono-label text-xs border border-stone-900 px-4 py-2 hover:bg-stone-900 hover:text-white transition"
        >Close</button>
      </div>
    </Modal>
  );
}

/* ──────────────────────────── Shared UI ──────────────────────────── */
function Modal({ title, onClose, children, testid, wide }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6"
      onClick={onClose}
      data-testid={testid}
    >
      <div
        className={`bg-white border hairline shadow-xl w-full ${wide ? "max-w-3xl" : "max-w-xl"} mt-10 p-6`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-serif text-xl text-stone-900">{title}</h3>
          <button
            onClick={onClose}
            data-testid="stock-modal-close"
            className="mono-label text-xs text-stone-500 hover:text-stone-900"
          >Close ✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mono-label text-[11px] text-stone-600 block mb-1">{label}</span>
      {children}
    </label>
  );
}
